import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ShieldCheck, Mail, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import MarketingLayout from "@/components/MarketingLayout";
import { api } from "@/lib/api";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your account email");
    setBusy(true);
    try {
      await api.post("/deletion-request", { email: email.trim(), reason: reason.trim() || undefined });
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Could not submit your request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout>
      <div className="fade-up" data-testid="delete-account-page">
        {/* Hero */}
        <div className="ss-card" style={{ padding: 34, background: "var(--brand)", color: "#fff" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.14)", borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
            <Trash2 size={14} style={{ color: "var(--gold)" }} /> Account deletion
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 800, marginTop: 16, lineHeight: 1.2 }}>Delete your SouqStream account</h1>
          <p style={{ marginTop: 14, fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,.9)", maxWidth: 640 }}>
            You can permanently delete your account and personal data from SouqStream. Choose the option that fits you below.
          </p>
        </div>

        {/* Fastest path: in-app */}
        <div className="ss-card" style={{ padding: 24, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}><ShieldCheck size={19} /></span>
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>Delete instantly (recommended)</h2>
          </div>
          <div style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.7 }}>
            If you can still sign in, the fastest way is to delete your account yourself: go to{" "}
            <b>Profile → Danger zone → Delete my account</b>. It removes your account and personal data immediately.
            <div style={{ marginTop: 14 }}>
              <button className="ss-btn ss-btn-primary" onClick={() => navigate("/login")} data-testid="delete-goto-login">Sign in to delete now</button>
            </div>
          </div>
        </div>

        {/* Request form */}
        <div className="ss-card" style={{ padding: 24, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}><Mail size={19} /></span>
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>Can't sign in? Request deletion</h2>
          </div>
          {done ? (
            <div style={{ textAlign: "center", padding: "16px 0" }} data-testid="delete-request-success">
              <CheckCircle2 size={44} style={{ color: "var(--success)" }} />
              <div style={{ fontWeight: 800, fontSize: 20, marginTop: 8 }}>Request received</div>
              <p className="ss-muted" style={{ marginTop: 6, maxWidth: 480, marginInline: "auto", lineHeight: 1.6 }}>
                We've emailed you a confirmation and will process your deletion within 30 days.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.7 }}>
                Enter the email tied to your account and we'll delete your account and personal data. We may contact you
                to verify the request.
              </div>
              <div>
                <label className="ss-label">Account email</label>
                <div className="ss-field"><Mail size={16} className="ss-muted" /><input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="delete-request-email" /></div>
              </div>
              <div>
                <label className="ss-label">Reason (optional)</label>
                <div className="ss-field"><input placeholder="Tell us why you're leaving (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="delete-request-reason" /></div>
              </div>
              <button className="ss-btn ss-btn-block" style={{ background: "var(--red)", color: "#fff" }} disabled={busy} data-testid="delete-request-submit">
                <Trash2 size={16} /> {busy ? "Submitting…" : "Request account deletion"}
              </button>
            </form>
          )}
        </div>

        {/* What gets deleted */}
        <div className="ss-card" style={{ padding: 24, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}><AlertTriangle size={19} /></span>
            <h2 style={{ fontSize: 20, fontWeight: 800 }}>What happens to your data</h2>
          </div>
          <div style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.7 }}>
            <p style={{ margin: 0 }}><b>Deleted:</b> your profile, saved items, offers, messages, invite codes, and other personal data.</p>
            <p style={{ margin: "10px 0 0" }}><b>Kept but anonymized:</b> past order and payment records, which we must retain for legal, tax, and accounting purposes — your name and address are removed from them.</p>
            <p style={{ margin: "10px 0 0" }}>See our{" "}
              <span onClick={() => navigate("/privacy")} style={{ color: "var(--brand)", fontWeight: 700, cursor: "pointer" }} data-testid="delete-privacy-link">Privacy Policy</span>{" "}for full details.
            </p>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
